/**
 * Rehype plugin that prepares parsed markdown for a CSS-driven reveal.
 *
 * Every run of text is cut into chunks of `charsPerTick` characters and each
 * chunk wrapped in a span carrying its tick number as a custom property. CSS
 * turns that number into an animation delay, so the text appears a chunk at a
 * time with nothing running per frame — the document is parsed once, and React
 * does not re-render while it plays.
 *
 * The chunks are counted across the whole document rather than per text node,
 * so a run that ends mid-chunk is continued by the next one and the cadence
 * stays even across bold text, links and list items.
 *
 * Ticks count *rendered* characters, while the caller sizes the animation from
 * the length of the markdown *source*. Syntax the parser consumes is therefore
 * never waited for, and the reveal can only finish early, never late.
 */

export const REVEAL_CHUNK_CLASS = 'reveal-chunk';
export const REVEAL_TICK_PROPERTY = '--reveal-tick';

interface HastNode {
    type: string;
    tagName?: string;
    value?: string;
    children?: HastNode[];
    properties?: Record<string, unknown>;
}

const chunkTextNode = (
    value: string,
    charsPerTick: number,
    charactersSoFar: number,
): { nodes: HastNode[]; charactersConsumed: number } => {
    const nodes: HastNode[] = [];
    // Cut by code point, not by `String` index: an emoji is two UTF-16 units, so
    // slicing between them would leave a lone surrogate at the end of one chunk
    // and another at the start of the next, each rendering as `�`. The spans
    // outlive the reveal, so that would be permanent rather than a flicker.
    const characters = Array.from(value);
    let consumed = charactersSoFar;
    let index = 0;

    while (index < characters.length) {
        // Fill the tick already in progress before starting a new one.
        const remainingInTick = charsPerTick - (consumed % charsPerTick);
        const take = Math.min(remainingInTick, characters.length - index);

        nodes.push({
            type: 'element',
            tagName: 'span',
            properties: {
                className: [REVEAL_CHUNK_CLASS],
                style: `${REVEAL_TICK_PROPERTY}:${Math.floor(consumed / charsPerTick)}`,
            },
            children: [
                {
                    type: 'text',
                    value: characters.slice(index, index + take).join(''),
                },
            ],
        });

        consumed += take;
        index += take;
    }

    return { nodes, charactersConsumed: consumed };
};

const rehypeRevealTicks =
    ({ charsPerTick }: { charsPerTick: number }) =>
    (tree: HastNode) => {
        // Each chunk has to advance the count, so a pace below one character a
        // tick would walk backwards and never reach the end of a run. The tree
        // comes back untouched instead, which is the same as no reveal at all.
        if (charsPerTick < 1) return;

        let charactersSoFar = 0;

        const visit = (node: HastNode) => {
            if (!node.children?.length) return;

            const children: HastNode[] = [];
            node.children.forEach((child) => {
                // A parse puts whitespace-only text nodes between block
                // elements — a newline before each `<li>`, and between the
                // parts of a table. Wrapping one would put a span where a span
                // is not valid content and give it a caret of its own, so it is
                // left alone and costs no ticks.
                if (child.type !== 'text' || !child.value.trim()) {
                    visit(child);
                    children.push(child);
                    return;
                }

                const chunked = chunkTextNode(
                    child.value,
                    charsPerTick,
                    charactersSoFar,
                );
                charactersSoFar = chunked.charactersConsumed;
                children.push(...chunked.nodes);
            });

            node.children = children;
        };

        visit(tree);
    };

export default rehypeRevealTicks;
